import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import { arrayOf, company, isString, json, MEMBERS_TIMEOUT_MS, record } from '../data/members'
import type { DictionaryCopy, Lang } from '../i18n'

/**
 * A dictionary axis a company edits: the idea, stored as hypothesis, and the visual
 * (GRO-170), the message and the offer (GRO-171).
 */
export type Axis = 'idea' | 'visual' | 'message' | 'offer'

/** One value of `GET /api/dictionaries/{axis}`. */
export interface HypothesisValue {
  id: string
  /** The one name a value is created with (PRD 0006, D13): the Russian label. */
  name: string
  labelEn: string
  labelRu: string
  /** `null` when unset; the reporting falls back to English. */
  labelUk: string | null
  claim: string | null
  archived: boolean
  /** Seeded with the demo data: read-only, never assignable. */
  demo: boolean
  createdAt: string
  /** Creatives that carry this value now. */
  creatives: number
}

/**
 * What a new value is created with: one non-empty name, which the server writes to all
 * three labels. A claim is kept for an idea and ignored for a visual.
 */
export interface HypothesisDraft {
  name: string
  claim: string | null
}

/** Any subset of the fields; `null` clears the Ukrainian label or the claim. */
export interface HypothesisPatch {
  labelEn?: string
  labelRu?: string
  labelUk?: string | null
  claim?: string | null
  archived?: boolean
}

/**
 * How a dictionary or assignment call ended. The classes of `data/members.ts`, plus
 * `invalid`: the `422` these routes answer for an empty label, a demo, archived or
 * unknown value, or a creative outside the company, with the route's code when the body
 * carried one.
 */
export type WriteOutcome<T> =
  | { status: 'ok'; body: T }
  | { status: 'stale' }
  | { status: 'expired' }
  | { status: 'notFound' }
  | { status: 'forbidden' }
  | { status: 'invalid'; code: string | null }
  | { status: 'unreachable' }
  | { status: 'unavailable' }

/**
 * How a create-and-assign from the assign bar ended (GRO-158): at the create, or at the
 * assignment that followed a created value.
 */
export type CreateOutcome =
  | { stage: 'create'; outcome: WriteOutcome<HypothesisValue> }
  | { stage: 'assign'; value: HypothesisValue; outcome: WriteOutcome<{ updated: number }> }

export function dictionaryUrl(axis: Axis): string {
  return `/api/dictionaries/${axis}`
}

/**
 * The code of an `{"error": "<code>"}` body. A body the router rejected before the
 * handler ran is plain text, so anything else reads as no code.
 */
function errorCode(text: string): string | null {
  try {
    const code = record(JSON.parse(text))?.error
    return isString(code) ? code : null
  } catch {
    return null
  }
}

/**
 * The members client's request, with its session-generation guard and its `401` report,
 * reading `422` as its own class. A call injects `fetchImpl` in tests; the product passes
 * nothing and uses the global.
 */
export async function request<T>(
  url: string,
  init: RequestInit,
  parse: (body: unknown) => T | null,
  fetchImpl: typeof fetch = fetch,
): Promise<WriteOutcome<T>> {
  const generation = authGeneration()
  let response: Response
  let text: string
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      signal: AbortSignal.any([authSignal(), AbortSignal.timeout(MEMBERS_TIMEOUT_MS)]),
      ...init,
    })
    text = await response.text()
  } catch {
    return generation === authGeneration() ? { status: 'unreachable' } : { status: 'stale' }
  }
  if (generation !== authGeneration()) return { status: 'stale' }
  if (response.status === 401) {
    reportSessionExpired()
    return { status: 'expired' }
  }
  if (response.status === 404) return { status: 'notFound' }
  if (response.status === 403) return { status: 'forbidden' }
  if (response.status === 422) return { status: 'invalid', code: errorCode(text) }
  if (response.status === 503) return { status: 'unreachable' }
  if (!response.ok) return { status: 'unavailable' }
  let body: unknown = null
  try {
    body = text === '' ? null : JSON.parse(text)
  } catch {
    return { status: 'unavailable' }
  }
  const parsed = parse(body)
  return parsed === null ? { status: 'unavailable' } : { status: 'ok', body: parsed }
}

const optionalString = (value: unknown): value is string | null => value === null || isString(value)

export function valueOf(value: unknown): HypothesisValue | null {
  const v = record(value)
  if (v === null) return null
  const { id, name, labelEn, labelRu, labelUk, claim, archived, demo, createdAt, creatives } = v
  if (!isString(id) || !isString(labelEn) || !isString(labelRu) || !isString(createdAt)) return null
  // A server from before D13 answers no name; its Russian label is the one a value keeps.
  const named = name ?? labelRu
  if (!isString(named)) return null
  // A server that leaves the optional fields out means "unset", not a broken answer.
  const uk = labelUk ?? null
  const text = claim ?? null
  if (!optionalString(uk) || !optionalString(text)) return null
  if (typeof archived !== 'boolean' || typeof demo !== 'boolean' || typeof creatives !== 'number') return null
  return { id, name: named, labelEn, labelRu, labelUk: uk, claim: text, archived, demo, createdAt, creatives }
}

export function listValues(
  companyId: string,
  axis: Axis,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<HypothesisValue[]>> {
  return request(`${dictionaryUrl(axis)}?${company(companyId)}`, {}, (body) => arrayOf(record(body)?.values, valueOf), fetchImpl)
}

export function addValue(
  companyId: string,
  axis: Axis,
  draft: HypothesisDraft,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<HypothesisValue>> {
  return request(`${dictionaryUrl(axis)}?${company(companyId)}`, json('POST', draft), valueOf, fetchImpl)
}

export function updateHypothesis(
  companyId: string,
  valueId: string,
  patch: HypothesisPatch,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<HypothesisValue>> {
  return request(
    `${dictionaryUrl('idea')}/${encodeURIComponent(valueId)}?${company(companyId)}`,
    json('PATCH', patch),
    valueOf,
    fetchImpl,
  )
}

/** The label a value reads under in `lang`: Ukrainian falls back to English, as the reporting does. */
export function valueLabel(value: HypothesisValue, lang: Lang): string {
  return lang === 'en' ? value.labelEn : lang === 'ru' ? value.labelRu : value.labelUk ?? value.labelEn
}

/** What the assign picker offers: neither an archived value nor a seeded one. */
export function assignable(values: HypothesisValue[]): HypothesisValue[] {
  return values.filter((value) => !value.archived && !value.demo)
}

const labelsOf = (value: HypothesisValue): string[] =>
  [value.labelEn, value.labelRu, value.labelUk].filter((label): label is string => label !== null)
    .map((label) => label.trim().toLocaleLowerCase())

/**
 * Whether a typed name is already one of the offered values, in any of its three labels:
 * trimmed and case-insensitive. The assign bar offers "Create hypothesis" only when it is not.
 */
export function matchesOffered(name: string, values: HypothesisValue[]): boolean {
  const wanted = name.trim().toLocaleLowerCase()
  return values.some((value) => labelsOf(value).includes(wanted))
}

/**
 * Whether the picker keeps a value for a search: every value for an empty one, otherwise a
 * value with the search inside any of its three labels, so a label typed in another
 * language still finds it.
 */
export function searchMatches(search: string, value: HypothesisValue): boolean {
  const wanted = search.trim().toLocaleLowerCase()
  return wanted === '' || labelsOf(value).some((label) => label.includes(wanted))
}

/** One dictionary write, as the shell issues it for a screen. Only an idea is edited. */
export type DictionaryOp =
  | { kind: 'add'; axis: Axis; draft: HypothesisDraft }
  | { kind: 'update'; id: string; patch: HypothesisPatch }

export function writeValue(companyId: string, op: DictionaryOp, fetchImpl?: typeof fetch): Promise<WriteOutcome<HypothesisValue>> {
  return op.kind === 'add'
    ? addValue(companyId, op.axis, op.draft, fetchImpl)
    : updateHypothesis(companyId, op.id, op.patch, fetchImpl)
}

/**
 * Whether a write changes a label the reporting shows. A new value has no creatives yet,
 * and the claim and the archive flag never reach the reporting, so only an edit that
 * carries a label does.
 */
export function relabels(op: DictionaryOp): boolean {
  return op.kind === 'update' && ('labelEn' in op.patch || 'labelRu' in op.patch || 'labelUk' in op.patch)
}

/** The `422` codes a person can act on, each with its own line; any other reads as `invalid`. */
const INVALID_LINES: Record<
  string,
  | 'required' | 'demoValue' | 'archivedValue' | 'unknownValue' | 'unknownCreatives' | 'badVersion' | 'noHypothesis'
  | 'badSelection' | 'demoCreatives' | 'mixedKinds' | 'assignmentConflict' | 'techniqueConflict'
  | 'messageConflict' | 'offerConflict'
> = {
  empty_label: 'required',
  demo_value: 'demoValue',
  archived_value: 'archivedValue',
  unknown_value: 'unknownValue',
  unknown_creatives: 'unknownCreatives',
  bad_version: 'badVersion',
  no_hypothesis: 'noHypothesis',
  bad_selection: 'badSelection',
  demo_creatives: 'demoCreatives',
  mixed_kinds: 'mixedKinds',
  assignment_conflict: 'assignmentConflict',
  technique_conflict: 'techniqueConflict',
  message_conflict: 'messageConflict',
  offer_conflict: 'offerConflict',
}

/**
 * The line a refused or failed write shows; `null` for an answer that belongs to a session
 * already gone. The dialog and the assign bar meet different `422` codes, and each reads
 * only its own, so one map serves both.
 */
export function failureLine(outcome: WriteOutcome<unknown>, copy: DictionaryCopy): string | null {
  switch (outcome.status) {
    case 'ok':
    case 'stale':
    case 'expired':
      return null
    case 'forbidden':
      return copy.forbidden
    case 'notFound':
      return copy.notFound
    case 'invalid':
      return outcome.code !== null && Object.hasOwn(INVALID_LINES, outcome.code)
        ? copy[INVALID_LINES[outcome.code]]
        : copy.invalid
    default:
      return copy.failed
  }
}
